/*
 * @providesModule CommonStyles
 *
 * Comprehensive utility styles for common layout patterns, spacing, sizing, etc.
 * Uses app theme system for consistent colors, fonts, and styling
 *
 * USAGE:
 * Import and use within StyleSheet.create() definitions, NOT as inline styles
 *
 * Basic usage:
 * container: { ...CommonStyles.flex1, ...CommonStyles.bgPrimary }
 *
 * Theme-aware usage:
 * container: { ...CommonStyles.flex1, ...CommonStyles.bgPrimary, ...CommonStyles.fontRegular }
 *
 * Dynamic theme access:
 * container: { ...CommonStyles.flex1, ...CommonStyles.getThemeStyle('viewPrimaryColor') }
 *
 * Available theme-aware styles:
 * - Background colors: bgPrimary, bgSecondary, bgTertiary, bgHighlight, bgCell, etc.
 * - Text colors: textCash, textTicketz
 * - Fonts: fontPrimary, fontRegular, fontBold, etc.
 * - Border radius: roundedThemePrimary, roundedThemeSecondary, roundedThemeTertiary
 * - Border widths: borderThemePrimary, borderThemeSecondary, borderThemeTertiary
 * - Shadows: shadowTheme
 */

// LAYOUT UTILITIES

// Flex
export const flex0 = {flex: 0};
export const flex1 = {flex: 1};
export const flex2 = {flex: 2};
export const flex3 = {flex: 3};
export const flex4 = {flex: 4};

// Flex Direction
export const flexRow = {flexDirection: 'row' as const};
export const flexCol = {flexDirection: 'column' as const};
export const flexRowReverse = {flexDirection: 'row-reverse' as const};
export const flexColReverse = {flexDirection: 'column-reverse' as const};

// Justify Content
export const justifyStart = {justifyContent: 'flex-start' as const};
export const justifyEnd = {justifyContent: 'flex-end' as const};
export const justifyCenter = {justifyContent: 'center' as const};
export const justifyBetween = {justifyContent: 'space-between' as const};
export const justifyAround = {justifyContent: 'space-around' as const};
export const justifyEvenly = {justifyContent: 'space-evenly' as const};

// Align Items
export const itemsStart = {alignItems: 'flex-start' as const};
export const itemsEnd = {alignItems: 'flex-end' as const};
export const itemsCenter = {alignItems: 'center' as const};
export const itemsStretch = {alignItems: 'stretch' as const};
export const itemsBaseline = {alignItems: 'baseline' as const};

// Align Self
export const selfStart = {alignSelf: 'flex-start' as const};
export const selfEnd = {alignSelf: 'flex-end' as const};
export const selfCenter = {alignSelf: 'center' as const};
export const selfStretch = {alignSelf: 'stretch' as const};
export const selfBaseline = {alignSelf: 'baseline' as const};

// Flex Wrap
export const flexWrap = {flexWrap: 'wrap' as const};
export const flexNoWrap = {flexWrap: 'nowrap' as const};

// POSITIONING UTILITIES

// Position
export const positionRelative = {position: 'relative' as const};
export const positionAbsolute = {position: 'absolute' as const};

// Absolute positioning shortcuts
export const absoluteFill = {position: 'absolute' as const, top: 0, left: 0, right: 0, bottom: 0};
export const absoluteTop = {position: 'absolute' as const, top: 0, left: 0, right: 0};
export const absoluteBottom = {position: 'absolute' as const, bottom: 0, left: 0, right: 0};
export const absoluteLeft = {position: 'absolute' as const, top: 0, left: 0, bottom: 0};
export const absoluteRight = {position: 'absolute' as const, top: 0, right: 0, bottom: 0};

// Top, Right, Bottom, Left
export const top0 = {top: 0};
export const top1 = {top: 4};
export const top2 = {top: 8};
export const top3 = {top: 12};
export const top4 = {top: 16};
export const top5 = {top: 20};
export const top6 = {top: 24};
export const top8 = {top: 32};
export const top10 = {top: 40};
export const top12 = {top: 48};
export const top16 = {top: 64};
export const top20 = {top: 80};

export const right0 = {right: 0};
export const right1 = {right: 4};
export const right2 = {right: 8};
export const right3 = {right: 12};
export const right4 = {right: 16};
export const right5 = {right: 20};
export const right6 = {right: 24};
export const right8 = {right: 32};
export const right10 = {right: 40};
export const right12 = {right: 48};
export const right16 = {right: 64};
export const right20 = {right: 80};

export const bottom0 = {bottom: 0};
export const bottom1 = {bottom: 4};
export const bottom2 = {bottom: 8};
export const bottom3 = {bottom: 12};
export const bottom4 = {bottom: 16};
export const bottom5 = {bottom: 20};
export const bottom6 = {bottom: 24};
export const bottom8 = {bottom: 32};
export const bottom10 = {bottom: 40};
export const bottom12 = {bottom: 48};
export const bottom16 = {bottom: 64};
export const bottom20 = {bottom: 80};

export const left0 = {left: 0};
export const left1 = {left: 4};
export const left2 = {left: 8};
export const left3 = {left: 12};
export const left4 = {left: 16};
export const left5 = {left: 20};
export const left6 = {left: 24};
export const left8 = {left: 32};
export const left10 = {left: 40};
export const left12 = {left: 48};
export const left16 = {left: 64};
export const left20 = {left: 80};

// SPACING UTILITIES (4px scale)

// Margin
export const m0 = {margin: 0};
export const m1 = {margin: 4};
export const m2 = {margin: 8};
export const m3 = {margin: 12};
export const m4 = {margin: 16};
export const m5 = {margin: 20};
export const m6 = {margin: 24};
export const m8 = {margin: 32};
export const m10 = {margin: 40};
export const m12 = {margin: 48};
export const m16 = {margin: 64};
export const m20 = {margin: 80};

// Margin Horizontal
export const mx0 = {marginHorizontal: 0};
export const mx1 = {marginHorizontal: 4};
export const mx2 = {marginHorizontal: 8};
export const mx3 = {marginHorizontal: 12};
export const mx4 = {marginHorizontal: 16};
export const mx5 = {marginHorizontal: 20};
export const mx6 = {marginHorizontal: 24};
export const mx8 = {marginHorizontal: 32};
export const mx10 = {marginHorizontal: 40};
export const mx12 = {marginHorizontal: 48};
export const mx16 = {marginHorizontal: 64};
export const mx20 = {marginHorizontal: 80};

// Margin Vertical
export const my0 = {marginVertical: 0};
export const my1 = {marginVertical: 4};
export const my2 = {marginVertical: 8};
export const my3 = {marginVertical: 12};
export const my4 = {marginVertical: 16};
export const my5 = {marginVertical: 20};
export const my6 = {marginVertical: 24};
export const my8 = {marginVertical: 32};
export const my10 = {marginVertical: 40};
export const my12 = {marginVertical: 48};
export const my16 = {marginVertical: 64};
export const my20 = {marginVertical: 80};

// Margin Top
export const mt0 = {marginTop: 0};
export const mt1 = {marginTop: 4};
export const mt1_5 = {marginTop: 6};
export const mt2 = {marginTop: 8};
export const mt3 = {marginTop: 12};
export const mt4 = {marginTop: 16};
export const mt5 = {marginTop: 20};
export const mt6 = {marginTop: 24};
export const mt8 = {marginTop: 32};
export const mt10 = {marginTop: 40};
export const mt12 = {marginTop: 48};
export const mt16 = {marginTop: 64};
export const mt20 = {marginTop: 80};

// Margin Right
export const mr0 = {marginRight: 0};
export const mr1 = {marginRight: 4};
export const mr2 = {marginRight: 8};
export const mr3 = {marginRight: 12};
export const mr4 = {marginRight: 16};
export const mr5 = {marginRight: 20};
export const mr6 = {marginRight: 24};
export const mr8 = {marginRight: 32};
export const mr10 = {marginRight: 40};
export const mr12 = {marginRight: 48};
export const mr16 = {marginRight: 64};
export const mr20 = {marginRight: 80};

// Margin Bottom
export const mb0 = {marginBottom: 0};
export const mb1 = {marginBottom: 4};
export const mb2 = {marginBottom: 8};
export const mb3 = {marginBottom: 12};
export const mb4 = {marginBottom: 16};
export const mb5 = {marginBottom: 20};
export const mb6 = {marginBottom: 24};
export const mb8 = {marginBottom: 32};
export const mb10 = {marginBottom: 40};
export const mb12 = {marginBottom: 48};
export const mb16 = {marginBottom: 64};
export const mb20 = {marginBottom: 80};

// Margin Left
export const ml0 = {marginLeft: 0};
export const ml1 = {marginLeft: 4};
export const ml2 = {marginLeft: 8};
export const ml3 = {marginLeft: 12};
export const ml4 = {marginLeft: 16};
export const ml5 = {marginLeft: 20};
export const ml6 = {marginLeft: 24};
export const ml8 = {marginLeft: 32};
export const ml10 = {marginLeft: 40};
export const ml12 = {marginLeft: 48};
export const ml16 = {marginLeft: 64};
export const ml20 = {marginLeft: 80};

// Padding
export const p0 = {padding: 0};
export const p1 = {padding: 4};
export const p2 = {padding: 8};
export const p3 = {padding: 12};
export const p4 = {padding: 16};
export const p5 = {padding: 20};
export const p6 = {padding: 24};
export const p8 = {padding: 32};
export const p10 = {padding: 40};
export const p12 = {padding: 48};
export const p16 = {padding: 64};
export const p20 = {padding: 80};

// Padding Horizontal
export const px0 = {paddingHorizontal: 0};
export const px1 = {paddingHorizontal: 4};
export const px2 = {paddingHorizontal: 8};
export const px3 = {paddingHorizontal: 12};
export const px4 = {paddingHorizontal: 16};
export const px5 = {paddingHorizontal: 20};
export const px6 = {paddingHorizontal: 24};
export const px8 = {paddingHorizontal: 32};
export const px10 = {paddingHorizontal: 40};
export const px12 = {paddingHorizontal: 48};
export const px16 = {paddingHorizontal: 64};
export const px20 = {paddingHorizontal: 80};

// Padding Vertical
export const py0 = {paddingVertical: 0};
export const py1 = {paddingVertical: 4};
export const py2 = {paddingVertical: 8};
export const py3 = {paddingVertical: 12};
export const py4 = {paddingVertical: 16};
export const py5 = {paddingVertical: 20};
export const py6 = {paddingVertical: 24};
export const py8 = {paddingVertical: 32};
export const py10 = {paddingVertical: 40};
export const py12 = {paddingVertical: 48};
export const py16 = {paddingVertical: 64};
export const py20 = {paddingVertical: 80};

// Padding Top
export const pt0 = {paddingTop: 0};
export const pt1 = {paddingTop: 4};
export const pt2 = {paddingTop: 8};
export const pt3 = {paddingTop: 12};
export const pt4 = {paddingTop: 16};
export const pt5 = {paddingTop: 20};
export const pt6 = {paddingTop: 24};
export const pt8 = {paddingTop: 32};
export const pt10 = {paddingTop: 40};
export const pt12 = {paddingTop: 48};
export const pt16 = {paddingTop: 64};
export const pt20 = {paddingTop: 80};
export const pt28_5 = {paddingTop: 114};

// Padding Right
export const pr0 = {paddingRight: 0};
export const pr1 = {paddingRight: 4};
export const pr2 = {paddingRight: 8};
export const pr3 = {paddingRight: 12};
export const pr4 = {paddingRight: 16};
export const pr5 = {paddingRight: 20};
export const pr6 = {paddingRight: 24};
export const pr8 = {paddingRight: 32};
export const pr10 = {paddingRight: 40};
export const pr12 = {paddingRight: 48};
export const pr16 = {paddingRight: 64};
export const pr20 = {paddingRight: 80};

// Padding Bottom
export const pb0 = {paddingBottom: 0};
export const pb1 = {paddingBottom: 4};
export const pb2 = {paddingBottom: 8};
export const pb3 = {paddingBottom: 12};
export const pb4 = {paddingBottom: 16};
export const pb5 = {paddingBottom: 20};
export const pb6 = {paddingBottom: 24};
export const pb8 = {paddingBottom: 32};
export const pb10 = {paddingBottom: 40};
export const pb12 = {paddingBottom: 48};
export const pb16 = {paddingBottom: 64};
export const pb20 = {paddingBottom: 80};

// Padding Left
export const pl0 = {paddingLeft: 0};
export const pl1 = {paddingLeft: 4};
export const pl2 = {paddingLeft: 8};
export const pl3 = {paddingLeft: 12};
export const pl4 = {paddingLeft: 16};
export const pl5 = {paddingLeft: 20};
export const pl6 = {paddingLeft: 24};
export const pl8 = {paddingLeft: 32};
export const pl10 = {paddingLeft: 40};
export const pl12 = {paddingLeft: 48};
export const pl16 = {paddingLeft: 64};
export const pl20 = {paddingLeft: 80};

// SIZING UTILITIES

// Width
export const w0 = {width: 0};
export const wAuto = {width: 'auto' as const};
export const wFull = {width: '100%'};
export const w1_2 = {width: '50%'};
export const w1_3 = {width: '33.333333%'};
export const w2_3 = {width: '66.666667%'};
export const w1_4 = {width: '25%'};
export const w3_4 = {width: '75%'};
export const w1_5 = {width: '20%'};
export const w2_5 = {width: '40%'};
export const w3_5 = {width: '60%'};
export const w4_5 = {width: '80%'};

// Fixed widths
export const w10 = {width: 40};
export const w12 = {width: 48};
export const w16 = {width: 64};
export const w20 = {width: 80};
export const w24 = {width: 96};
export const w32 = {width: 128};
export const w40 = {width: 160};
export const w48 = {width: 192};
export const w56 = {width: 224};
export const w64 = {width: 256};

// Height
export const h0 = {height: 0};
export const hAuto = {height: 'auto' as const};
export const hFull = {height: '100%'};
export const h1_2 = {height: '50%'};
export const h1_3 = {height: '33.333333%'};
export const h2_3 = {height: '66.666667%'};
export const h1_4 = {height: '25%'};
export const h3_4 = {height: '75%'};
export const h1_5 = {height: '20%'};
export const h2_5 = {height: '40%'};
export const h3_5 = {height: '60%'};
export const h4_5 = {height: '80%'};

// Fixed heights
export const h10 = {height: 40};
export const h12 = {height: 48};
export const h16 = {height: 64};
export const h20 = {height: 80};
export const h24 = {height: 96};
export const h32 = {height: 128};
export const h40 = {height: 160};
export const h48 = {height: 192};
export const h56 = {height: 224};
export const h64 = {height: 256};

// Min/Max Width
export const minW0 = {minWidth: 0};
export const minWFull = {minWidth: '100%'};
export const maxWFull = {maxWidth: '100%'};
export const maxWNone = {maxWidth: 'none' as const};

// Min/Max Height
export const minH0 = {minHeight: 0};
export const minHFull = {minHeight: '100%'};
export const maxHFull = {maxHeight: '100%'};
export const maxHNone = {maxHeight: 'none' as const};

// BACKGROUND COLORS

// Transparent
export const bgTransparent = {backgroundColor: 'transparent'};

// Basic colors
export const bgWhite = {backgroundColor: '#FFFFFF'};
export const bgBlack = {backgroundColor: '#000000'};
export const bgGray = {backgroundColor: '#6B7280'};
export const bgGrayLight = {backgroundColor: '#F3F4F6'};
export const bgGrayDark = {backgroundColor: '#374151'};

// Utility colors
export const bgSuccess = {backgroundColor: '#10B981'};
export const bgWarning = {backgroundColor: '#F59E0B'};
export const bgError = {backgroundColor: '#EF4444'};
export const bgInfo = {backgroundColor: '#06B6D4'};

// TEXT COLORS

// Basic text colors
export const textWhite = {color: '#FFFFFF'};
export const textBlack = {color: '#000000'};
export const textGray = {color: '#6B7280'};
export const textGrayLight = {color: '#9CA3AF'};
export const textGrayDark = {color: '#374151'};

// Utility text colors
export const textPrimary = {color: '#3B82F6'};
export const textSecondary = {color: '#6366F1'};
export const textSuccess = {color: '#10B981'};
export const textWarning = {color: '#F59E0B'};
export const textError = {color: '#EF4444'};
export const textInfo = {color: '#06B6D4'};

// Utility generic toast color
export const genericToastSuccess = {color: '#4fa93f'};
export const genericToastError = {color: '#DD6952'};
export const genericToastWarning = {color: '#fda102'};
export const genericToastInfo = {color: '#6fc2e5'};

// TEXT ALIGNMENT

export const textLeft = {textAlign: 'left' as const};
export const textCenter = {textAlign: 'center' as const};
export const textRight = {textAlign: 'right' as const};
export const textJustify = {textAlign: 'justify' as const};

// TEXT DECORATION

export const underline = {textDecorationLine: 'underline' as const};
export const lineThrough = {textDecorationLine: 'line-through' as const};
export const noUnderline = {textDecorationLine: 'none' as const};

// Note: fontSemiBold and fontExtraBold are added dynamically in SkillzStyle
export const fontSemiBoldFont = {fontFamily: 'HelveticaNeue-Medium'};
export const fontExtraBoldFont = {fontFamily: 'HelveticaNeue-Bold'};

// FONT WEIGHT
export const fontThin = {fontFamily: 'HelveticaNeue-Thin'};
export const fontLight = {fontFamily: 'HelveticaNeue-Light'};
export const fontNormal = {fontFamily: 'HelveticaNeue'};
export const fontMedium = {fontFamily: 'HelveticaNeue-Medium'};
export const fontSemibold = {fontFamily: 'HelveticaNeue-Medium'};
export const fontBold = {fontFamily: 'HelveticaNeue-Bold'};
export const fontExtrabold = {fontFamily: 'HelveticaNeue-Bold'};
export const fontBlack = {fontFamily: 'HelveticaNeue-Bold'};

// FONT SIZE

export const text10 = {fontSize: 10};
export const text12 = {fontSize: 12};
export const text14 = {fontSize: 14};
export const text16 = {fontSize: 16};
export const text18 = {fontSize: 18};
export const text20 = {fontSize: 20};
export const text22 = {fontSize: 22};
export const text24 = {fontSize: 24};
export const text26 = {fontSize: 26};
export const text30 = {fontSize: 30};
export const text36 = {fontSize: 36};
export const text48 = {fontSize: 48};
export const text60 = {fontSize: 60};

// BORDER UTILITIES

// Border Width
export const border0 = {borderWidth: 0};
export const border = {borderWidth: 1};
export const border2 = {borderWidth: 2};
export const border4 = {borderWidth: 4};
export const border8 = {borderWidth: 8};

// Border Color
export const borderTransparent = {borderColor: 'transparent'};
export const borderWhite = {borderColor: '#FFFFFF'};
export const borderBlack = {borderColor: '#000000'};
export const borderGray = {borderColor: '#6B7280'};
export const borderGrayLight = {borderColor: '#D1D5DB'};
export const borderGrayDark = {borderColor: '#374151'};

// Border Radius (Theme-aware)
export const roundedNone = {borderRadius: 0};
export const roundedSm = {borderRadius: 2};
export const rounded = {borderRadius: 4};
export const roundedMd = {borderRadius: 6};
export const roundedLg = {borderRadius: 8};
export const roundedXl = {borderRadius: 12};
export const rounded2Xl = {borderRadius: 16};
export const rounded3Xl = {borderRadius: 24};
export const roundedFull = {borderRadius: 9999};

// Border Style
export const borderSolid = {borderStyle: 'solid' as const};
export const borderDashed = {borderStyle: 'dashed' as const};
export const borderDotted = {borderStyle: 'dotted' as const};

// SHADOW UTILITIES

export const shadowSm = {
  shadowColor: '#000000',
  shadowOffset: {width: 0, height: 1},
  shadowOpacity: 0.05,
  shadowRadius: 2,
  elevation: 1,
};

export const shadow = {
  shadowColor: '#000000',
  shadowOffset: {width: 0, height: 1},
  shadowOpacity: 0.1,
  shadowRadius: 3,
  elevation: 2,
};

export const shadowMd = {
  shadowColor: '#000000',
  shadowOffset: {width: 0, height: 4},
  shadowOpacity: 0.1,
  shadowRadius: 6,
  elevation: 3,
};

export const shadowLg = {
  shadowColor: '#000000',
  shadowOffset: {width: 0, height: 10},
  shadowOpacity: 0.1,
  shadowRadius: 15,
  elevation: 4,
};

export const shadowXl = {
  shadowColor: '#000000',
  shadowOffset: {width: 0, height: 20},
  shadowOpacity: 0.1,
  shadowRadius: 25,
  elevation: 5,
};

export const shadow2Xl = {
  shadowColor: '#000000',
  shadowOffset: {width: 0, height: 25},
  shadowOpacity: 0.25,
  shadowRadius: 50,
  elevation: 6,
};

export const shadowNone = {
  shadowColor: 'transparent',
  shadowOffset: {width: 0, height: 0},
  shadowOpacity: 0,
  shadowRadius: 0,
  elevation: 0,
};

// OPACITY

export const opacity0 = {opacity: 0};
export const opacity25 = {opacity: 0.25};
export const opacity35 = {opacity: 0.35};
export const opacity50 = {opacity: 0.5};
export const opacity60 = {opacity: 0.6};
export const opacity75 = {opacity: 0.75};
export const opacity100 = {opacity: 1};

// Z-INDEX

export const z0 = {zIndex: 0};
export const z10 = {zIndex: 10};
export const z20 = {zIndex: 20};
export const z30 = {zIndex: 30};
export const z40 = {zIndex: 40};
export const z50 = {zIndex: 50};

// OVERFLOW

export const overflowVisible = {overflow: 'visible' as const};
export const overflowHidden = {overflow: 'hidden' as const};
export const overflowScroll = {overflow: 'scroll' as const};

// COMMON PATTERNS

// Centering
export const centerContent = {
  justifyContent: 'center' as const,
  alignItems: 'center' as const,
};

export const centerHorizontal = {
  alignItems: 'center' as const,
};

export const centerVertical = {
  justifyContent: 'center' as const,
};

// Full screen
export const fullScreen = {
  width: '100%',
  height: '100%',
};

// Row with space between
export const rowBetween = {
  flexDirection: 'row' as const,
  justifyContent: 'space-between' as const,
  alignItems: 'center' as const,
};

// Row with center alignment
export const rowCenter = {
  flexDirection: 'row' as const,
  justifyContent: 'center' as const,
  alignItems: 'center' as const,
};

// Column with center alignment
export const colCenter = {
  flexDirection: 'column' as const,
  justifyContent: 'center' as const,
  alignItems: 'center' as const,
};

// Absolute center
export const absoluteCenter = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: [{translateX: -50}, {translateY: -50}],
};

// Export all utilities as a default object for easier importing
const CommonStyles = {
  // Layout
  flex0,
  flex1,
  flex2,
  flex3,
  flex4,
  flexRow,
  flexCol,
  flexRowReverse,
  flexColReverse,
  justifyStart,
  justifyEnd,
  justifyCenter,
  justifyBetween,
  justifyAround,
  justifyEvenly,
  itemsStart,
  itemsEnd,
  itemsCenter,
  itemsStretch,
  itemsBaseline,
  selfStart,
  selfEnd,
  selfCenter,
  selfStretch,
  selfBaseline,
  flexWrap,
  flexNoWrap,

  // Positioning
  positionRelative,
  positionAbsolute,
  absoluteFill,
  absoluteTop,
  absoluteBottom,
  absoluteLeft,
  absoluteRight,
  top0,
  top1,
  top2,
  top3,
  top4,
  top5,
  top6,
  top8,
  top10,
  top12,
  top16,
  top20,
  right0,
  right1,
  right2,
  right3,
  right4,
  right5,
  right6,
  right8,
  right10,
  right12,
  right16,
  right20,
  bottom0,
  bottom1,
  bottom2,
  bottom3,
  bottom4,
  bottom5,
  bottom6,
  bottom8,
  bottom10,
  bottom12,
  bottom16,
  bottom20,
  left0,
  left1,
  left2,
  left3,
  left4,
  left5,
  left6,
  left8,
  left10,
  left12,
  left16,
  left20,

  // Spacing
  m0,
  m1,
  m2,
  m3,
  m4,
  m5,
  m6,
  m8,
  m10,
  m12,
  m16,
  m20,
  mx0,
  mx1,
  mx2,
  mx3,
  mx4,
  mx5,
  mx6,
  mx8,
  mx10,
  mx12,
  mx16,
  mx20,
  my0,
  my1,
  my2,
  my3,
  my4,
  my5,
  my6,
  my8,
  my10,
  my12,
  my16,
  my20,
  mt0,
  mt1,
  mt1_5,
  mt2,
  mt3,
  mt4,
  mt5,
  mt6,
  mt8,
  mt10,
  mt12,
  mt16,
  mt20,
  mr0,
  mr1,
  mr2,
  mr3,
  mr4,
  mr5,
  mr6,
  mr8,
  mr10,
  mr12,
  mr16,
  mr20,
  mb0,
  mb1,
  mb2,
  mb3,
  mb4,
  mb5,
  mb6,
  mb8,
  mb10,
  mb12,
  mb16,
  mb20,
  ml0,
  ml1,
  ml2,
  ml3,
  ml4,
  ml5,
  ml6,
  ml8,
  ml10,
  ml12,
  ml16,
  ml20,
  p0,
  p1,
  p2,
  p3,
  p4,
  p5,
  p6,
  p8,
  p10,
  p12,
  p16,
  p20,
  px0,
  px1,
  px2,
  px3,
  px4,
  px5,
  px6,
  px8,
  px10,
  px12,
  px16,
  px20,
  py0,
  py1,
  py2,
  py3,
  py4,
  py5,
  py6,
  py8,
  py10,
  py12,
  py16,
  py20,
  pt0,
  pt1,
  pt2,
  pt3,
  pt4,
  pt5,
  pt6,
  pt8,
  pt10,
  pt12,
  pt16,
  pt20,
  pt28_5,
  pr0,
  pr1,
  pr2,
  pr3,
  pr4,
  pr5,
  pr6,
  pr8,
  pr10,
  pr12,
  pr16,
  pr20,
  pb0,
  pb1,
  pb2,
  pb3,
  pb4,
  pb5,
  pb6,
  pb8,
  pb10,
  pb12,
  pb16,
  pb20,
  pl0,
  pl1,
  pl2,
  pl3,
  pl4,
  pl5,
  pl6,
  pl8,
  pl10,
  pl12,
  pl16,
  pl20,

  // Sizing
  w0,
  wAuto,
  wFull,
  w1_2,
  w1_3,
  w2_3,
  w1_4,
  w3_4,
  w1_5,
  w2_5,
  w3_5,
  w4_5,
  w10,
  w12,
  w16,
  w20,
  w24,
  w32,
  w40,
  w48,
  w56,
  w64,
  h0,
  hAuto,
  hFull,
  h1_2,
  h1_3,
  h2_3,
  h1_4,
  h3_4,
  h1_5,
  h2_5,
  h3_5,
  h4_5,
  h10,
  h12,
  h16,
  h20,
  h24,
  h32,
  h40,
  h48,
  h56,
  h64,
  minW0,
  minWFull,
  maxWFull,
  maxWNone,
  minH0,
  minHFull,
  maxHFull,
  maxHNone,

  // Colors
  bgTransparent,
  bgWhite,
  bgBlack,
  bgGray,
  bgGrayLight,
  bgGrayDark,
  bgSuccess,
  bgWarning,
  bgError,
  bgInfo,
  textWhite,
  textBlack,
  textGray,
  textGrayLight,
  textGrayDark,
  textPrimary,
  textSecondary,
  textSuccess,
  textWarning,
  textError,
  textInfo,

  // Typography
  textLeft,
  textCenter,
  textRight,
  textJustify,
  underline,
  lineThrough,
  noUnderline,

  fontSemiBoldFont,
  fontExtraBoldFont,
  // Font weights
  fontThin,
  fontLight,
  fontNormal,
  fontMedium,
  fontSemibold,
  fontBold,
  fontExtrabold,
  fontBlack,
  // Font sizes
  text10,
  text12,
  text14,
  text16,
  text18,
  text20,
  text22,
  text24,
  text26,
  text30,
  text36,
  text48,
  text60,

  // Borders
  border0,
  border,
  border2,
  border4,
  border8,

  // Border colors
  borderTransparent,
  borderWhite,
  borderBlack,
  borderGray,
  borderGrayLight,
  borderGrayDark,
  // Border radius
  roundedNone,
  roundedSm,
  rounded,
  roundedMd,
  roundedLg,
  roundedXl,
  rounded2Xl,
  rounded3Xl,
  roundedFull,
  // Border styles
  borderSolid,
  borderDashed,
  borderDotted,

  // Effects
  shadowSm,
  shadow,
  shadowMd,
  shadowLg,
  shadowXl,
  shadow2Xl,
  shadowNone,

  // Opacity
  opacity0,
  opacity25,
  opacity50,
  opacity60,
  opacity75,
  opacity100,
  // Z-index
  z0,
  z10,
  z20,
  z30,
  z40,
  z50,
  // Overflow
  overflowVisible,
  overflowHidden,
  overflowScroll,

  // Common Patterns
  centerContent,
  centerHorizontal,
  centerVertical,
  fullScreen,
  rowBetween,
  rowCenter,
  colCenter,
  absoluteCenter,

  // generic toast reusable classes
  genericToastError,
  genericToastInfo,
  genericToastSuccess,
  genericToastWarning
};

export default CommonStyles;
